import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Download, ImageOff, Loader2, Search, Store } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthStore } from '@/features/auth/auth.store';
import { useCategories, useDepartments } from '@/features/catalog/catalog.api';
import { useDivisions } from '@/features/item-master/item-master.api';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ItemDrawer } from './item-drawer';
import { SharedStoresDialog } from './shared-stores-dialog';
import { PAGE_SIZE, useExportCatalog, useInventorySearch } from './inventory.api';
import { SORT, itemLabel, packBreakdown, type InventoryItem } from './types';

/**
 * One store's stock, item by item.
 *
 * Store-scoped by definition — a quantity on hand belongs to a `BusinessItem`,
 * not to a catalogue item — so the page asks for a store rather than showing a
 * global list that could not be acted on.
 *
 * Not carried over from the original, and worth knowing: the case-breakdown
 * flow (`inventory/breakdown`, splitting a case into units across related
 * items) and the catalogue export. Both are real features; neither is wired
 * here yet.
 */

const ANY = 'ANY';

/**
 * Catalogue taxonomy names.
 *
 * Departments and categories spell their Spanish field differently — a
 * department carries `name`, a category `nameSpa` — and neither matches the
 * `nameEn`/`nameEs` pair `localizedName` reads, so the shared helper does not
 * fit and a four-line local one is honest about why.
 */
function taxonomyLabel(
  node: { nameEng?: string | null; name?: string | null; nameSpa?: string | null },
  language: string
): string {
  const spanish = node.nameSpa ?? node.name ?? null;
  const preferred = language === 'en' ? node.nameEng : spanish;
  return preferred || node.nameEng || spanish || '';
}

export function InventoryPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const business = useAuthStore((state) => state.business);

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [division, setDivision] = useState(ANY);
  const [departmentId, setDepartmentId] = useState(ANY);
  const [categoryId, setCategoryId] = useState(ANY);
  const [sorting, setSorting] = useState(String(SORT.default));
  const [page, setPage] = useState(0);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const { data: divisions } = useDivisions();
  const { data: departments } = useDepartments();
  const { data: categories } = useCategories();

  const exportCatalog = useExportCatalog();

  const search = useInventorySearch(
    {
      businessId: business?.id ?? '',
      query,
      divisions: division === ANY ? [] : [division],
      groups: [],
      departmentId: departmentId === ANY ? null : departmentId,
      categoryId: categoryId === ANY ? null : categoryId,
      sorting: Number(sorting),
      page,
    },
    !!business
  );

  const rows = search.data?.result ?? [];
  const total = search.data?.total ?? 0;

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const fields: FilterField[] = [
    {
      id: 'division',
      label: t('inventory.division'),
      value: division,
      onChange: reset(setDivision),
      options: [
        { value: ANY, label: t('inventory.anyDivision') },
        ...(divisions ?? []).map((name) => ({ value: name, label: name })),
      ],
    },
    {
      id: 'department',
      label: t('inventory.department'),
      value: departmentId,
      onChange: reset(setDepartmentId),
      options: [
        { value: ANY, label: t('inventory.anyDepartment') },
        ...(departments ?? []).map((department) => ({
          value: department.id,
          label: taxonomyLabel(department, language),
        })),
      ],
    },
    {
      id: 'category',
      label: t('inventory.category'),
      value: categoryId,
      onChange: reset(setCategoryId),
      options: [
        { value: ANY, label: t('inventory.anyCategory') },
        ...(categories ?? []).map((category) => ({
          value: category.id,
          label: taxonomyLabel(category, language),
        })),
      ],
    },
    {
      id: 'sorting',
      label: t('inventory.sortBy'),
      value: sorting,
      onChange: reset(setSorting),
      sort: false,
      resetValue: String(SORT.default),
      options: [
        { value: String(SORT.default), label: t('inventory.sortDefault') },
        { value: String(SORT.ascending), label: t('inventory.sortLowest') },
        { value: String(SORT.descending), label: t('inventory.sortHighest') },
      ],
    },
  ];

  const Thumb = ({ item }: { item: InventoryItem }) =>
    item.itemImage ? (
      <img
        src={item.itemImage}
        alt=""
        className="bg-muted size-10 shrink-0 rounded-md object-contain"
      />
    ) : (
      <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md">
        <ImageOff className="size-3.5" />
      </div>
    );

  /**
   * The same count in boxes, for anything sold by the case.
   *
   * The original printed this in place of the raw number; both are useful and
   * they answer different questions, so the count leads and the box maths sits
   * under it.
   */
  const Packs = ({ item }: { item: InventoryItem }) => {
    const packs = packBreakdown(item.quantityOnHand, item.unitsPerPackage);
    if (!packs) return null;
    return (
      <p className="text-muted-foreground text-xs">
        {t('inventory.packsAndUnits', {
          packs: formatNumber(packs.packs),
          units: formatNumber(packs.units),
        })}
      </p>
    );
  };

  /** The two switches that decide whether a shopper can buy it at all. */
  const Flags = ({ item }: { item: InventoryItem }) => (
    <div className="flex flex-wrap gap-1">
      {!item.isVisible && (
        <Badge variant="outline">{t('inventory.hidden')}</Badge>
      )}
      {!item.isAvailable && (
        <Badge variant="secondary">{t('inventory.unavailable')}</Badge>
      )}
      {!item.affectsInventory && (
        <Badge variant="outline">{t('inventory.untracked')}</Badge>
      )}
    </div>
  );

  if (!business) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('inventory.title')}
          description={t('inventory.subtitle')}
        />
        <div className="text-muted-foreground flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
          <Store className="size-7" />
          <p className="max-w-sm text-sm text-pretty">
            {t('inventory.chooseStore')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventory.title')}
        description={t('inventory.subtitleFor', { store: business.name })}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={exportCatalog.isPending}
              onClick={() =>
                exportCatalog.mutate(
                  {
                    businessId: business.id,
                    divisions: division === ANY ? [] : [division],
                    departmentId: departmentId === ANY ? null : departmentId,
                    categoryId: categoryId === ANY ? null : categoryId,
                  },
                  {
                    onSuccess: () =>
                      toast.success(
                        query.trim()
                          ? t('inventory.exportedIgnoringSearch')
                          : t('inventory.exported')
                      ),
                    onError: (error) =>
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : t('inventory.exportFailed')
                      ),
                  }
                )
              }
            >
              {exportCatalog.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              <span className="hidden sm:inline">
                {t('inventory.export')}
              </span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSharing(true)}>
              <Store className="size-3.5" />
              <span className="hidden sm:inline">
                {t('inventory.sharedStores')}
              </span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('inventory.searchPlaceholder')}
                aria-label={t('inventory.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={fields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('inventory.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('inventory.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void search.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-10 shrink-0 rounded-md" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-5 w-12" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('inventory.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-14" />
                  <TableHead>{t('inventory.product')}</TableHead>
                  <TableHead>{t('inventory.where')}</TableHead>
                  <TableHead className="text-right">
                    {t('inventory.onHand')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('inventory.cost')}
                  </TableHead>
                  <TableHead>{t('inventory.flags')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Thumb item={item} />
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {itemLabel(item)}
                        </p>
                        <p className="text-muted-foreground truncate font-mono text-xs">
                          {item.upc}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="truncate">{item.superSectionName || '—'}</p>
                      <p className="text-muted-foreground truncate">
                        {item.sectionName}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          item.affectsInventory && item.quantityOnHand <= 0
                            ? 'text-destructive font-medium tabular-nums'
                            : 'font-medium tabular-nums'
                        }
                      >
                        {formatNumber(item.quantityOnHand)}
                      </span>
                      <Packs item={item} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {formatCurrency(item.cost)}
                    </TableCell>
                    <TableCell>
                      <Flags item={item} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setOpenItem(item.id)}>
                        {t('inventory.manage')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'space-y-2',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((item) => (
                <div
                  key={item.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start gap-3">
                    <Thumb item={item} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{itemLabel(item)}</p>
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {item.upc}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-medium tabular-nums">
                        {formatNumber(item.quantityOnHand)}
                      </p>
                      <Packs item={item} />
                    </div>
                  </div>
                  <Flags item={item} />
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setOpenItem(item.id)}
                  >
                    {t('inventory.manage')}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <ItemDrawer
        businessItemId={openItem}
        open={openItem !== null}
        onOpenChange={(next) => {
          if (!next) setOpenItem(null);
        }}
      />

      <SharedStoresDialog
        businessId={business.id}
        businessName={business.name}
        open={sharing}
        onOpenChange={setSharing}
      />
    </div>
  );
}
